import { CommercialProductSubType, EppSolutionResponse, OnlineModelCategory } from '../../generated/api/models.js';
import { Locale, getLocale, monthMsg, monthsMsg, t, vatMsg } from '../i18n';
import { userHasEmptyOrSMEPriceGroup } from './employeeUtils.js';
import { deepEqual, isNumber } from './objectUtils.js';
import { getPriceWithTax, getVatAsString } from './taxUtils';
import type { TotalSumsItem } from './commercialProductUtils';
import type {
  CommercialProduct,
  CompanyInfoResponse,
  EntityPrice,
  Offer,
  OnlineModelHeader,
  Price,
  PricesByType,
  SubscriptionDetails,
} from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { ActionsHistory } from '../../selfservice/common/store.js';

/**
 * Useful for finding out best price to use
 * oneTimeCharge populates only if there's price with 1 payment, payments don't populate in this scenario
 * monthlyRecurringCharge populates with the lowest value available determined by the highest payments value available
 * payments populate with the payment value of the lowest priced option
 * cheapest combination will be used to populate monthlyRecurringCharge and payments
 */
export const resolvePrices = (listOfPrices: Array<Price>): Price => {
  const resolveCharge = (m1?: number, m2?: number) => {
    if (m1 !== undefined && m2 !== undefined) {
      return m1 < m2 ? m1 : m2;
    }
    return m1 !== undefined ? m1 : m2;
  };
  const resolvePayments = (prices: Price[], m?: number) =>
    m ? prices.find(price => price?.monthlyRecurringCharge === m)?.payments : undefined;
  const mapPrice = (prices: Price[]): Price => {
    return prices
      .map(price => (price.payments === 1 ? { oneTimeCharge: price.oneTimeCharge } : price))
      .reduce((acc, cur) => {
        const monthlyRecurringCharge = resolveCharge(acc.monthlyRecurringCharge, cur.monthlyRecurringCharge);
        return {
          oneTimeCharge:
            cur.oneTimeCharge && (!cur.payments || cur.payments === 1)
              ? resolveCharge(acc.oneTimeCharge, cur.oneTimeCharge)
              : acc.oneTimeCharge,
          monthlyRecurringCharge: monthlyRecurringCharge,
          payments: resolvePayments([acc, cur], monthlyRecurringCharge),
        };
      }, {});
  };
  return mapPrice(listOfPrices);
};

export const getOnetimePriceFromCommercialProducts = (commercialProducts: CommercialProduct[]): number | undefined => {
  return commercialProducts.find(
    ({ productSubType, payments }) => productSubType !== CommercialProductSubType.EPP_DEVICE && payments === 1
  )?.oneTimeCharge;
};

export const getMonthlyRecurringChargeFromCommercialProducts = (
  commercialProducts: CommercialProduct[]
): number | undefined =>
  commercialProducts.find(({ payments, price }) => payments === 0 && price?.effectivePrice?.monthlyRecurringCharge)
    ?.price?.effectivePrice?.monthlyRecurringCharge;

/**
 * As per current information content-blocks should use DEVICE type
 */
export const selectPricesByDeviceType = (onlineModelHeader: OnlineModelHeader) => {
  const prices = onlineModelHeader.lowestPrices.find(
    priceByType => priceByType.productSubType === CommercialProductSubType.DEVICE
  )?.prices;
  return (prices && prices.length && resolvePrices(prices)) || {};
};

/**
 * We need to note the billingPeriod - at least YTT-products have billing period greater than 0
 */
export const getMonthlyPrice = (monthlyRecurringCharge?: number, billingPeriod?: number) => {
  return monthlyRecurringCharge ? monthlyRecurringCharge / (billingPeriod || 1) : undefined;
};

/**
 * SALES_PRODUCT uses monthly price multiplied by 12 for analytics impression event
 */
export const getComputationalAveragePriceForSubscription = (commercialProducts: CommercialProduct[]) => {
  return (
    Math.min(
      ...commercialProducts.map(o => getMonthlyPrice(o.monthlyRecurringCharge, o.billingPeriod)).filter(isNumber)
    ) * 12
  );
};

/**
 * Non SALES_PRODUCT uses onetime price for analytics impression event
 */
export const getCheapestOnetimePriceFromDeviceCommercialProducts = (commercialProducts: CommercialProduct[]) => {
  return Math.min(...commercialProducts.filter(o => o.oneTimeCharge && o.payments === 1).map(o => o.oneTimeCharge!));
};

/**
 * Products with productType SALES_PRODUCT require special handling
 */
export const getPriceForType = (offers: Offer[], category?: string) => {
  const commercialProducts = offers.flatMap(o => o.commercialProducts);
  if (category !== OnlineModelCategory.SALES_PRODUCT) {
    return getCheapestOnetimePriceFromDeviceCommercialProducts(commercialProducts);
  }
  return getComputationalAveragePriceForSubscription(commercialProducts);
};

export function priceToString(priceInCents?: number): string | undefined {
  return priceInCents === undefined ? undefined : (priceInCents / 100).toFixed(2);
}
export const eurosToCents = (priceInEuros = 0): number => {
  return Math.round(priceInEuros * 100);
};

export const centsToEuros = (priceInCents = 0): number => priceInCents / 100;

/**
 * Enter a number in cents, output (money) sum in euros with currency.
 *
 * @param value in cents
 * @param omitCurrency skip the post-euro char
 * @return sum in EUR
 */
export const formatSumToString = (value?: number, omitCurrency = false): string => {
  const val = Math.abs(value || 0);
  const i = Math.floor(val / 100); // Integer part
  const f = val - 100 * i; // Fraction part
  const iStr = `${i}`;
  let formattedString;
  if (iStr.length > 3) {
    formattedString = iStr.substr(-3, 3);
    for (let index = -3; index > -iStr.length; index -= 3) {
      const startIndex = iStr.length >= Math.abs(index - 3) ? index - 3 : -iStr.length;
      const len = iStr.length + startIndex > 3 ? 3 : Math.min(3, iStr.length + index);
      formattedString = iStr.substr(startIndex, len) + ' ' + formattedString;
    }
  } else {
    formattedString = iStr;
  }
  const fStr = f < 10 ? `0${f}` : `${f}`; // Additional pre-zero
  return `${formattedString},${fStr}${!omitCurrency ? ' €' : ''}`; // Currency if needed
};

export const formatSum = (value?: number, omitCurrency = false): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return formatSumToString(value, omitCurrency);
};

/**
 * Enter a number in cents, output (money) sum in euros with currency. Output with possible signum '-'.
 * By: Ville :O
 *
 * @param {number} value in cents
 * @param {boolean} omitCurrency skip the post-euro char
 * @return {string} in EUR
 */
export const formatSumWithSignum = (value?: number, omitCurrency?: boolean): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const sumStr = formatSum(value, omitCurrency);
  const signumStr = value < 0 ? '-' : '';
  return `${signumStr}${sumStr}`;
};

/**
 * Subscription API returns one-time and reoccurring charge in n.n format, which needs to be reformatted to to n,nn
 *
 * @param {number} value
 * @return {string} formatted price
 */
export const formatNumberToCurrency = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

/**
 * Parses price given by user as form input as a number
 * @param {string} value in formatted euro string such as 35,41
 * @return {number} price in cents such as 3541
 */
export const parseCurrencyToNumber = (value?: string): number | undefined =>
  value ? Number((Number(value.replace(',', '.')) * 100).toFixed(0)) : undefined;

/**
 * Format balance to negative format to indicate how much have been paid from total value.
 * E.g. if total is 1000 and balance is 100 then paid will be -900
 * and if total is 1000 and paid is -1000 then paid will be -2000
 * This is because positive balance is counted as there is something to pay and negative balance is counted that total is over paid.
 *
 * @param {number} total total number of invoice or maximum value that need to be paid.
 * @param {number} balance number indicating either how much is paid or how much need to be paid.
 * @param {boolean|undefined} omitCurrency indicating whether to skip post number 'EUR' currency
 * @return {string} in EUR
 */
export const formatBalance = (total: number, balance: number, omitCurrency?: boolean): string | undefined => {
  let value;
  // When positive its sum left to pay
  if (balance > 0) {
    value = Math.abs(balance - total) - Math.abs(balance - total) * 2; // Use times (*) 2 to negate the value
  } else if (balance < 0) {
    // When negative invoice has been over paid
    value = total + Math.abs(balance) - (total + Math.abs(balance)) * 2; // Use  times (*) 2 to negate the value
  } else {
    // When balance is 0, it is paid in full
    value = total - total * 2; // Use times (*) 2 to negate the value
  }

  return formatSum(value, omitCurrency);
};

const eppSolutionSettingsAllowPrice = (
  price?: Price,
  subType?: CommercialProductSubType,
  isEppCustomer?: boolean,
  companyInfo?: CompanyInfoResponse,
  user?: (AuthenticatedUser & ActionsHistory) | null
) => {
  if (isEppCustomer) {
    return (
      subType === CommercialProductSubType.EPP_DEVICE ||
      (price?.payments && price.payments > 1 && companyInfo?.eppSolution?.allowDevicesWithRecurringCharges) ||
      ((price?.payments === 1 || !price?.payments) && companyInfo?.eppSolution?.allowDevicesWithOneTimeCharges)
    );
  }
  if (!userHasEmptyOrSMEPriceGroup(user?.segmentPricingGroup)) {
    return subType !== CommercialProductSubType.EPP_DEVICE && (!price?.payments || price.payments === 1);
  }
  return subType !== CommercialProductSubType.EPP_DEVICE;
};

const accessoriesAllowPrice = (
  price: Price,
  isEppCustomer: boolean | undefined,
  user: (AuthenticatedUser & ActionsHistory) | null | undefined
) =>
  isEppCustomer || !userHasEmptyOrSMEPriceGroup(user?.segmentPricingGroup)
    ? price?.payments === 1 || !price?.payments
    : true;

const filterHeadersByEppSolution = (
  category: OnlineModelCategory,
  lowestPrices: PricesByType[],
  companyInfo?: CompanyInfoResponse,
  isEppCustomer?: boolean,
  user?: (AuthenticatedUser & ActionsHistory) | null
) =>
  lowestPrices.map(priceObject => {
    return {
      ...priceObject,
      prices:
        category === OnlineModelCategory.ACCESSORIES
          ? priceObject.prices.filter(price => accessoriesAllowPrice(price, isEppCustomer, user))
          : priceObject.prices.filter(price =>
              eppSolutionSettingsAllowPrice(price, priceObject.productSubType, isEppCustomer, companyInfo, user)
            ),
    };
  });

export const filterPricesByAccountSettings = (
  headers: OnlineModelHeader[],
  companyInfo?: CompanyInfoResponse,
  user?: (AuthenticatedUser & ActionsHistory) | null
): OnlineModelHeader[] =>
  headers.map(header => {
    const filteredPrices = filterHeadersByEppSolution(
      header.category,
      header.lowestPrices,
      companyInfo,
      companyInfo?.eppSolution?.eppSolutionStatus === EppSolutionResponse.EppSolutionStatusEnum.ACTIVE,
      user
    );
    return { ...header, lowestPrices: filteredPrices };
  });

/**
 * CompanyInfoResponse does not carry discountedPrices itself (it comes from a separate
 * DiscountedPricesResponse), but v2Router/v3Router mocks and tests attach it to companyInfo
 * to simulate the combined data available when computing discounts.
 */
// Some discount may set total to negative and negative values should not be shown to customer
export const ensureNonNegativeTotals = (totals: TotalSumsItem): TotalSumsItem => ({
  totalFixedTermCharge: Math.max(0, totals.totalFixedTermCharge),
  totalMonthlyRecurringCharge: Math.max(0, totals.totalMonthlyRecurringCharge),
  totalOneTimeCharge: Math.max(0, totals.totalOneTimeCharge),
  totalMonthlyRecurringChargeVat: Math.max(0, totals.totalMonthlyRecurringChargeVat),
  totalOneTimeChargeVat: Math.max(0, totals.totalOneTimeChargeVat),
});

export const isContractPrice = (entityPrice: EntityPrice | undefined): boolean =>
  !!entityPrice?.effectivePrice &&
  !!entityPrice?.listPrice &&
  !entityPrice?.campaign &&
  !deepEqual(entityPrice.effectivePrice, entityPrice.listPrice);

export const getRecurringPriceAsText = (monthlyRecurringCharge?: number, billingPeriod = 0) => {
  const monthText = billingPeriod ? `/${billingPeriod} ${t.XXVX(monthsMsg)}` : `/${t.XXVX(monthMsg)}`;
  return monthlyRecurringCharge ? `${formatSum(monthlyRecurringCharge)}${monthText}` : '—';
};

export const getPriceAsText = (
  charge?: number,
  listCharge?: number,
  periodic?: boolean,
  undefinedValue?: string,
  billingPeriod = 0,
  addVat = false,
  showVat = false
): string | undefined => {
  if (charge === undefined) {
    return undefinedValue || undefined;
  }
  const billingSuffix = billingPeriod ? `/${billingPeriod} ${t.XXVX(monthsMsg)}` : `/${t.XXVX(monthMsg)}`;
  const periodSuffix = periodic ? billingSuffix : '';
  const showListPrice = listCharge !== undefined && charge !== listCharge;
  const listPriceText = showListPrice ? ` (${t.LRAQ('reg.')} ${formatSum(listCharge)}${periodSuffix})` : '';
  const finalCharge = addVat ? getPriceWithTax(charge) : charge;
  const percentageFormat = getLocale() === Locale.EN ? '%' : ' %';
  const vatText = addVat
    ? ` (${t.U8AN('incl.')} ${t.UZ6X(vatMsg)} ${getVatAsString()}${percentageFormat})`
    : ` (${t.UZ6X(vatMsg)} 0${percentageFormat})`;
  return `${formatSum(finalCharge)}${periodSuffix}${listPriceText}${showVat ? vatText : ''}`;
};

export const getRecurringPriceAsTextFromDetails = (subDetails?: SubscriptionDetails): string | undefined => {
  if (subDetails?.monthlyRecurringCharge === 0) {
    return '—';
  }
  return getPriceAsText(subDetails?.monthlyRecurringCharge, subDetails?.monthlyRecurringListCharge, true, '—');
};

export const getEmployeePeriodicPriceAsText = (price: number | undefined): string => {
  if (price) {
    return getPriceAsText(price, undefined, true, '-', 0, true, true) || '-';
  } else {
    return '-';
  }
};

export const getPriceOrHyphen = (priceInEur: number | undefined, currency: string = '€'): string => {
  if (!priceInEur) {
    return `- ${currency}`;
  }
  return `${priceInEur} ${currency}`;
};
